/**
 * JT Islands Bootstrap
 *
 * Finds every [data-jt-island] element on the page and mounts the matching
 * React component into it, hydrating with props read from a JSON-encoded
 * data-jt-props attribute. Components are dynamic-imported so each island
 * type ships in its own chunk and only loads when present.
 *
 * To add a new island:
 *   1. Drop the component in src/components/<Name>.jsx
 *   2. Add it to ISLANDS below: 'island-key': () => import('./components/Name.jsx')
 *   3. PHP renders <div data-jt-island="island-key" data-jt-props="…json…">
 */

import './main.css';
import { createRoot } from 'react-dom/client';
import { useState, useEffect } from 'react';
import Lightbox from './components/Lightbox.jsx';

const ISLANDS = {
	// BIOS Lighting — registry layout, reuses the original CaseStudy.jsx
	// component (the JSX was already a 1:1 port of bios_lighting_case_study).
	// Phase 1 ACF migration (2026-05-03) routed BIOS through the registry so
	// the legacy single-case_study.php + ACF Field_Groups files could be
	// deleted.
	'case-study-bios-lighting': () => import( './components/CaseStudy.jsx' ),

	// Per-layout case-study islands (registry pattern).
	'case-study-dempsey':         () => import( './case-studies/dempsey.jsx' ),
	'case-study-helix-electric':  () => import( './case-studies/helix-electric.jsx' ),
	'case-study-nuvasive':        () => import( './case-studies/nuvasive.jsx' ),
	'case-study-carnosyn':        () => import( './case-studies/carnosyn.jsx' ),
	'case-study-hensel-phelps':   () => import( './case-studies/hensel-phelps.jsx' ),
	'case-study-merlone-geier':   () => import( './case-studies/merlone-geier.jsx' ),
	'case-study-dexcom':          () => import( './case-studies/dexcom.jsx' ),
	'case-study-jp-cullen':       () => import( './case-studies/jp-cullen.jsx' ),
	'case-study-rachel-contracting': () => import( './case-studies/rachel-contracting.jsx' ),
	'case-study-jbs':             () => import( './case-studies/jbs.jsx' ),
	'case-study-seaspine':        () => import( './case-studies/seaspine.jsx' ),
	'case-study-dp-electric':     () => import( './case-studies/dp-electric.jsx' ),
	'case-study-advisent':        () => import( './case-studies/advisent.jsx' ),
	'case-study-rowan-electric':  () => import( './case-studies/rowan-electric.jsx' ),

	// Phase 2D-ext — legacy SEO-recovery case studies (sit at end of /case-studies/ via menu_order=100)
	'case-study-mccain-inc':           () => import( './case-studies/mccain-inc.jsx' ),
	'case-study-navigate-biopharma':   () => import( './case-studies/navigate-biopharma.jsx' ),
	'case-study-itan':                 () => import( './case-studies/itan.jsx' ),
	'case-study-ullman-sails':         () => import( './case-studies/ullman-sails.jsx' ),
	'case-study-san-diego-airport':    () => import( './case-studies/san-diego-airport.jsx' ),
	'case-study-dowling-yahnke':       () => import( './case-studies/dowling-yahnke.jsx' ),
	'case-study-circadia':             () => import( './case-studies/circadia.jsx' ),
	'case-study-usa-service-dogs':     () => import( './case-studies/usa-service-dogs.jsx' ),
	'case-study-ymca':                 () => import( './case-studies/ymca.jsx' ),
	'case-study-gryphon':              () => import( './case-studies/gryphon.jsx' ),
	'case-study-dudek':                () => import( './case-studies/dudek.jsx' ),
	'case-study-dewitt':               () => import( './case-studies/dewitt.jsx' ),
	'case-study-set':                  () => import( './case-studies/set.jsx' ),
	'case-study-southwest-strategies': () => import( './case-studies/southwest-strategies.jsx' ),
	'case-study-california-coast-credit-union': () => import( './case-studies/california-coast-credit-union.jsx' ),

	'logo-portfolio':        () => import( './components/LogoPortfolio.jsx' ),
	'case-studies-archive':  () => import( './components/CaseStudiesArchive.jsx' ),
	'testimonials':          () => import( './components/Testimonials.jsx' ),
	'contact-page':          () => import( './components/ContactPage.jsx' ),
};

async function mountOne( el ) {
	const name = el.getAttribute( 'data-jt-island' );
	const loader = ISLANDS[ name ];
	if ( ! loader ) {
		console.warn( `[jt-islands] No component registered for "${ name }"` );
		return;
	}

	let props = {};
	try {
		const raw = el.getAttribute( 'data-jt-props' );
		if ( raw ) {
			props = JSON.parse( raw );
		}
	} catch ( e ) {
		console.error( `[jt-islands ${ name }] failed to parse data-jt-props`, e );
	}

	try {
		const mod = await loader();
		const Component = mod.default;
		createRoot( el ).render( <Component { ...props } /> );
		el.setAttribute( 'data-mounted', 'true' );
	} catch ( e ) {
		console.error( `[jt-islands ${ name }] mount failed`, e );
	}
}

function mount() {
	const islands = document.querySelectorAll( '[data-jt-island]' );
	islands.forEach( mountOne );
	setupLightbox( islands );
}

/* ============== Lightbox manager ==============
 * Mounts a single Lightbox React root on <body> the first time a case-study
 * island appears. Uses event delegation: a single click handler on body
 * intercepts <img> clicks inside any case-study island, opens the Lightbox
 * with the clicked image's resolved src (currentSrc — picks the WebP source
 * when a <picture> wrapper is present).
 *
 * Heuristics for what's lightbox-able:
 *  - Must be inside [data-jt-island^="case-study-"]
 *  - Image must be at least 150px on its longest side (skips tiny icons)
 *  - Skips images inside <a>, <button>, [data-no-lightbox] (don't hijack
 *    intentional click targets)
 *
 * To opt out per-image: add data-no-lightbox attribute to the <img> or any
 * ancestor element.
 */
function setupLightbox( islands ) {
	const hasCaseStudy = Array.from( islands ).some(
		( el ) => ( el.getAttribute( 'data-jt-island' ) || '' ).startsWith( 'case-study-' )
	);
	if ( ! hasCaseStudy ) return;
	if ( document.body.dataset.jtLightboxReady === '1' ) return;
	document.body.dataset.jtLightboxReady = '1';

	const host = document.createElement( 'div' );
	host.className = 'jt-lightbox-host';
	document.body.appendChild( host );

	let openFn = null;
	function LightboxRoot() {
		const [ state, setState ] = useState( { src: null, alt: '' } );
		useEffect( () => {
			openFn = ( src, alt ) => setState( { src, alt } );
		}, [] );
		return (
			<Lightbox
				src={ state.src }
				alt={ state.alt }
				onClose={ () => setState( { src: null, alt: '' } ) }
			/>
		);
	}
	createRoot( host ).render( <LightboxRoot /> );

	document.body.addEventListener( 'click', ( event ) => {
		const img = event.target.closest && event.target.closest( 'img' );
		if ( ! img || ! openFn ) return;
		const island = img.closest( '[data-jt-island]' );
		if ( ! island || ! ( island.getAttribute( 'data-jt-island' ) || '' ).startsWith( 'case-study-' ) ) return;
		if ( img.closest( 'a, button' ) ) return;
		if ( img.closest( '[data-no-lightbox]' ) ) return;
		const longestEdge = Math.max(
			img.naturalWidth || img.width || 0,
			img.naturalHeight || img.height || 0
		);
		if ( longestEdge < 150 ) return;
		event.preventDefault();
		openFn( img.currentSrc || img.src, img.alt );
	}, true );
}

/* Add a small style hint on hover so users know images are clickable.
 * Scoped via a body class so it only kicks in once the manager wires up. */
const style = document.createElement( 'style' );
style.textContent = `
[data-jt-island^="case-study-"] img:not([data-no-lightbox]):not(a img):not(button img) {
	cursor: zoom-in;
	transition: filter 0.2s ease;
}
[data-jt-island^="case-study-"] img:not([data-no-lightbox]):not(a img):not(button img):hover {
	filter: brightness(1.04);
}
`;
document.head.appendChild( style );

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', mount );
} else {
	mount();
}
